/**
 * Job Widget SDK
 * Universal JavaScript SDK for embedding job widgets
 */

// Core exports
export { JobWidget } from './core/widget';
export { APIClient } from './core/api';
export { OAuthHandler } from './core/oauth';
export { EventEmitter } from './core/events';
export { Logger } from './core/logger';

// Import for local usage
import { JobWidget } from './core/widget';
import { Logger } from './core/logger';

// Type exports
export type {
  WidgetConfig,
  WidgetType,
  WidgetState,
  WidgetEvents,
  WidgetError,
  Job,
  Application,
  User,
  ProfileIncompleteData,
  AuthToken,
  OAuthConfig,
  APIResponse,
  PaginatedResponse
} from './core/types';

// Version
export const VERSION = '1.0.0';

// Global initialization for browser
declare global {
  interface Window {
    JobWidget: typeof JobWidget;
    initJobWidget?: (config: import('./core/types').WidgetConfig) => Promise<import('./core/widget').JobWidget>;
  }
}

// Auto-initialize if script tag has data attributes
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  // Make JobWidget available globally
  window.JobWidget = JobWidget;

  // Check if we're on the OAuth callback page
  if (window.location.pathname.includes('/oauth-callback')) {
    JobWidget.handleOAuthCallback();
  } else if (window.location.pathname === '/job-widget/callback') {
    JobWidget.handleCallback();
  }

  // Helper function for easy initialization
  window.initJobWidget = async (config: import('./core/types').WidgetConfig) => {
    const widget = new JobWidget(config);
    await widget.init();
    return widget;
  };

  // Auto-initialize from script tag attributes
  document.addEventListener('DOMContentLoaded', () => {
    const script = document.currentScript || document.querySelector('script[data-job-widget]');

    if (script && script.hasAttribute('data-job-widget')) {
      const config: any = {
        container: script.getAttribute('data-container') || '#job-widget',
        tenantId: script.getAttribute('data-tenant-id'),
        apiKey: script.getAttribute('data-api-key'),
        clientId: script.getAttribute('data-client-id'),
        redirectUri: script.getAttribute('data-redirect-uri'),
        type: script.getAttribute('data-widget-type') as import('./core/types').WidgetType || 'job-list',
        apiUrl: script.getAttribute('data-api-url'),
        theme: script.getAttribute('data-theme'),
        primaryColor: script.getAttribute('data-primary-color'),
        locale: script.getAttribute('data-locale')
      };

      // Remove undefined values
      Object.keys(config).forEach(key => {
        if (config[key] === null || config[key] === undefined) {
          delete config[key];
        }
      });

      // Check required fields
      if (config.tenantId && config.apiKey && config.clientId && config.redirectUri) {
        window.initJobWidget!(config).catch(error => {
          Logger.error('Failed to initialize job widget', error);
        });
      }
    }

  });
}

// Default export for convenience
export default JobWidget;